import { useState, useEffect, useRef } from 'react';

/**
 * Returns `true` when the header should be hidden (user is scrolling down),
 * `false` when it should be visible (user is scrolling up or near the top).
 *
 * @param {boolean} enabled  - set `false` to disable (always visible)
 * @param {number}  threshold - minimum scroll delta (px) before toggling
 */
const useHideOnScroll = (enabled = true, threshold = 10) => {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return;
    }

    lastY.current = window.scrollY;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY.current;

        // `lastY` is an anchor, not the previous frame: it only moves once the
        // scroll passes the threshold, so slow scrolls (a few px per frame)
        // accumulate instead of being dropped frame by frame.
        if (y <= 0) {
          // At the very top — always show
          setHidden(false);
          lastY.current = y;
        } else if (dy > threshold) {
          // Scrolling down past threshold
          setHidden(true);
          lastY.current = y;
        } else if (dy < -threshold) {
          // Scrolling up past threshold
          setHidden(false);
          lastY.current = y;
        }

        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled, threshold]);

  return hidden;
};

export default useHideOnScroll;
